import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import daygridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType } from "docx";
import { saveAs } from "file-saver";

const AcademicCalendar = () => {
  const [planTitle, setPlanTitle] = useState(() => localStorage.getItem('planTitle') || 'Akademik Plan 2026');
  const [draftEvents, setDraftEvents] = useState(() => JSON.parse(localStorage.getItem('draftEvents')) || []);
  const [calendarEvents, setCalendarEvents] = useState(() => JSON.parse(localStorage.getItem('calendarEvents')) || []);
  const [newName, setNewName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [duration, setDuration] = useState('');
  const [eventColor, setEventColor] = useState('#3498db');
  const [editingId, setEditingId] = useState(null);
  const draggableRef = useRef(null);

  const holidays = [
    { title: 'Yılbaşı', start: '2026-01-01', display: 'background', color: '#ffcccc' },
    { title: '23 Nisan Ulusal Egemenlik', start: '2026-04-23', backgroundColor: '#e74c3c' },
    { title: '1 Mayıs İşçi Bayramı', start: '2026-05-01', backgroundColor: '#e74c3c' },
    { title: '19 Mayıs Atatürk\'ü Anma', start: '2026-05-19', backgroundColor: '#e74c3c' },
    { title: 'Kurban Bayramı Arifesi', start: '2026-05-26', backgroundColor: '#f1c40f', textColor: '#000' },
    { title: 'Kurban Bayramı', start: '2026-05-27', end: '2026-05-31', backgroundColor: '#f1c40f', textColor: '#000' },
    { title: '15 Temmuz Demokrasi B.', start: '2026-07-15', backgroundColor: '#e74c3c' },
    { title: '30 Ağustos Zafer Bayramı', start: '2026-08-30', backgroundColor: '#e74c3c' },
    { title: '29 Ekim Cumhuriyet Bayramı', start: '2026-10-29', backgroundColor: '#e74c3c' },
    { title: 'Ramazan Bayramı 2027', start: '2027-03-09', end: '2027-03-12', backgroundColor: '#f1c40f', textColor: '#000' }
  ];

  useEffect(() => {
    localStorage.setItem('draftEvents', JSON.stringify(draftEvents));
    localStorage.setItem('calendarEvents', JSON.stringify(calendarEvents));
    localStorage.setItem('planTitle', planTitle);
  }, [draftEvents, calendarEvents, planTitle]);

  useEffect(() => {
    const draggableEl = document.getElementById('external-events-list');
    if (draggableRef.current) draggableRef.current.destroy();
    if (draggableEl) {
      draggableRef.current = new Draggable(draggableEl, {
        itemSelector: '.fc-event',
        eventData: (eventEl) => ({
          title: eventEl.getAttribute('data-title'),
          id: eventEl.getAttribute('data-id'),
          backgroundColor: eventEl.getAttribute('data-color'),
        })
      });
    }
  }, [draftEvents]);

  // --- WORD (DOCX) DIŞA AKTARMA FONKSİYONU ---
  const exportWord = () => {
    const tableRows = [
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Etkinlik Adı", bold: true })] })] }),
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Başlangıç", bold: true })] })] }),
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Bitiş", bold: true })] })] }),
        ],
      }),
    ];

    calendarEvents.forEach((ev) => {
      tableRows.push(
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph(ev.title)] }),
            new TableCell({ children: [new Paragraph(ev.start)] }),
            new TableCell({ children: [new Paragraph(ev.end || ev.start)] }),
          ],
        })
      );
    });

    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            children: [new TextRun({ text: planTitle, bold: true, size: 32 })],
            spacing: { after: 400 },
          }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: tableRows,
          }),
        ],
      }],
    });

    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `${planTitle}.docx`);
    });
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.text(planTitle, 14, 15);
    const tableData = calendarEvents.map(ev => [ev.title, `${ev.start} / ${ev.end}`]);
    autoTable(doc, { startY: 25, head: [['Etkinlik', 'Tarih']], body: tableData, styles: { font: 'helvetica' } });
    doc.save(`${planTitle}.pdf`);
  };

  const saveToDrafts = () => {
    if (!newName || !startDate || !endDate) return alert("Eksik bilgi!");
    const newId = editingId || Math.random().toString(36).substr(2, 9);
    const newDraft = { id: newId, title: newName, start: startDate, end: endDate, color: eventColor };
    if (editingId) {
      setDraftEvents(draftEvents.map(ev => ev.id === editingId ? newDraft : ev));
      setEditingId(null);
    } else {
      setDraftEvents([...draftEvents, newDraft]);
    }
    setNewName(''); setStartDate(''); setEndDate(''); setDuration('');
  };

  const handleDrop = (info) => {
    const draftId = info.draggedEl.getAttribute('data-id');
    const originalDraft = draftEvents.find(d => d.id === draftId);
    if (!originalDraft) return;
    let d = new Date(originalDraft.end); d.setDate(d.getDate() + 1);
    const newEvent = { 
      id: originalDraft.id, title: originalDraft.title, start: originalDraft.start, 
      end: d.toISOString().split('T')[0], allDay: true, backgroundColor: originalDraft.color
    };
    setCalendarEvents(prev => [...prev, newEvent]);
  };

  return (
    <div style={{ display: 'flex', height: '100vh', padding: '10px', backgroundColor: '#f4f7f6', fontFamily: 'Arial', overflow: 'hidden' }}>
      <div style={{ width: '400px', backgroundColor: '#fff', padding: '20px', borderRadius: '15px', marginRight: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.1)', overflowY: 'auto' }}>
        <input value={planTitle} onChange={e => setPlanTitle(e.target.value)} style={titleInp} />
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginBottom: '10px' }}>
          <button onClick={() => {
             const blob = new Blob([JSON.stringify({ planTitle, draftEvents, calendarEvents })], { type: 'application/json' });
             const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${planTitle}.json`; a.click();
          }} style={actBtn}>JSON İndir</button>
          <button onClick={exportPDF} style={{ ...actBtn, backgroundColor: '#e67e22' }}>PDF Al</button>
          <button onClick={exportWord} style={{ ...actBtn, backgroundColor: '#2980b9' }}>Word Al</button>
          <button onClick={() => {
            if (window.confirm("Tüm veriler silinsin mi?")) {
              setDraftEvents([]); setCalendarEvents([]); localStorage.clear();
            }
          }} style={{ ...actBtn, backgroundColor: '#c0392b' }}>Tümünü Sil</button>
        </div>

        <div style={formBox}>
          <label style={lbl}>Olay Adı</label>
          <input type="text" value={newName} onChange={e => setNewName(e.target.value)} style={inp} />
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}><label style={lbl}>Renk</label><input type="color" value={eventColor} onChange={e => setEventColor(e.target.value)} style={{...inp, height: '35px'}} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>Süre</label><input type="number" value={duration} onChange={e => {
                setDuration(e.target.value);
                if(startDate) {
                    let d = new Date(startDate);
                    d.setDate(d.getDate() + (parseInt(e.target.value) - 1));
                    setEndDate(d.toISOString().split('T')[0]);
                }
            }} style={inp} /></div>
          </div>
          <label style={lbl}>Başlangıç - Bitiş</label>
          <div style={{ display: 'flex', gap: '5px' }}>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inp} />
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inp} />
          </div>
          <button onClick={saveToDrafts} style={{ ...btn, backgroundColor: editingId ? '#f39c12' : '#27ae60' }}>{editingId ? "Güncelle" : "Taslağa Ekle"}</button>
        </div>

        <div id="external-events-list">
          {draftEvents.map(event => (
            <div key={event.id} className="fc-event" data-id={event.id} data-title={event.title} data-color={event.color}
              style={{ padding: '10px', backgroundColor: event.color, color: 'white', borderRadius: '8px', marginBottom: '8px', cursor: 'grab', fontSize: '13px' }}>
              <strong>{event.title}</strong>
              <div style={{ marginTop: '5px' }}>
                <button onClick={() => { setEditingId(event.id); setNewName(event.title); setStartDate(event.start); setEndDate(event.end); setEventColor(event.color); }} style={sBtn}>Düzenle</button>
                <button onClick={() => setDraftEvents(draftEvents.filter(ev => ev.id !== event.id))} style={sBtn}>Sil</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, backgroundColor: '#fff', padding: '15px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.1)' }}>
        <FullCalendar plugins={[daygridPlugin, interactionPlugin]} initialView="dayGridMonth" locale="tr" firstDay={1} height="100%"
          editable={true} droppable={true} events={[...calendarEvents, ...holidays]} drop={handleDrop}
          eventClick={(info) => { if(!info.event.display && window.confirm("Silinsin mi?")) setCalendarEvents(prev => prev.filter(ev => ev.id !== info.event.id)); }}
        />
      </div>
    </div>
  );
};

const titleInp = { width: '100%', border: 'none', borderBottom: '2px solid #3498db', fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', textAlign: 'center', outline: 'none' };
const actBtn = { width: '100%', padding: '8px', backgroundColor: '#2c3e50', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' };
const formBox = { backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '10px', marginBottom: '15px', border: '1px solid #eee' };
const lbl = { fontSize: '12px', fontWeight: 'bold', color: '#555', display: 'block' };
const inp = { width: '100%', padding: '6px', marginBottom: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '13px' };
const btn = { width: '100%', padding: '10px', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' };
const sBtn = { padding: '2px 6px', border: 'none', borderRadius: '3px', cursor: 'pointer', backgroundColor: 'rgba(0,0,0,0.2)', color: 'white', fontSize: '10px', marginRight: '5px' };

export default AcademicCalendar;